/**
 * Branded SVG icons for each category. Inline so they scale crisply and
 * can be animated without extra HTTP round-trips.
 *
 * - Instagram Verification — Instagram's official 8-pointed verified-badge
 *   silhouette with their brand gradient + white check.
 * - Telegram Premium — multi-ray "diamond" star matching the in-app icon,
 *   blue-violet gradient.
 * - Telegram Stars — 5-point gold star with sparkles.
 * - Telegram Gifts — wrapped gift box with bow, pink/gold ribbon.
 */
import type { CategoryKind } from '@/services/catalog.service';

export function CategoryIcon({
  kind,
  size = 56,
}: {
  kind: CategoryKind;
  size?: number;
}) {
  switch (kind) {
    case 'instagram_verification':
      return <InstagramBadge size={size} />;
    case 'telegram_premium':
      return <TelegramPremium size={size} />;
    case 'telegram_stars':
      return <TelegramStars size={size} />;
    case 'telegram_gifts':
      return <TelegramGifts size={size} />;
    case 'telegram_nft':
      return <TelegramNft size={size} />;
    case 'telegram_verification':
      return <TelegramVerify size={size} />;
    default:
      return null;
  }
}

/* ---------------- Telegram NFT — kolleksion gem (tier bo'yicha rang) ---------------- */
export type NftTier = 'standard' | 'rare' | 'legendary' | 'custom';

// SKU sxemasi: TG-NFT-STD-*, TG-NFT-RARE-*, TG-NFT-LGD-*, TG-NFT-CUSTOM
export function nftTierFromSku(sku: string | undefined | null): NftTier {
  if (!sku) return 'standard';
  if (sku.includes('CUSTOM')) return 'custom';
  if (sku.includes('LGD') || sku.includes('LEGEND')) return 'legendary';
  if (sku.includes('RARE')) return 'rare';
  return 'standard';
}

const NFT_GEM: Record<NftTier, { a: string; b: string; c: string; ring: string; glow: string }> = {
  standard: { a: '#E0F2FE', b: '#38BDF8', c: '#0369A1', ring: '#7DD3FC', glow: 'rgba(56,189,248,0.6)' },
  rare: { a: '#F5D0FE', b: '#A855F7', c: '#4C1D95', ring: '#FDE68A', glow: 'rgba(168,85,247,0.65)' },
  legendary: { a: '#FFF7CC', b: '#F5B301', c: '#7A4A00', ring: '#FFE9A3', glow: 'rgba(245,179,1,0.7)' },
  custom: { a: '#FFE4F1', b: '#F472B6', c: '#7C3AED', ring: '#FDE68A', glow: 'rgba(244,114,182,0.6)' },
};

export function NftGem({ size = 56, tier = 'rare' }: { size?: number; tier?: NftTier }) {
  const g = NFT_GEM[tier];
  const id = `nft-${tier}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      aria-hidden
      style={{ filter: `drop-shadow(0 8px 18px ${g.glow}) drop-shadow(0 2px 4px rgba(0,0,0,0.35))` }}
    >
      <defs>
        <linearGradient id={`${id}-face`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={g.a} />
          <stop offset="50%" stopColor={g.b} />
          <stop offset="100%" stopColor={g.c} />
        </linearGradient>
        <linearGradient id={`${id}-ring`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={g.ring} />
          <stop offset="50%" stopColor="#FFFFFF" stopOpacity="0.9" />
          <stop offset="100%" stopColor={g.ring} />
        </linearGradient>
        <radialGradient id={`${id}-shine`} cx="35%" cy="25%" r="60%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.95" />
          <stop offset="60%" stopColor="#FFFFFF" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* Tashqi olti burchak halqa (holografik) */}
      <path d="M50 3 L91 26 L91 74 L50 97 L9 74 L9 26 Z" fill={`url(#${id}-ring)`} opacity="0.95" />
      <path d="M50 8 L86 29 L86 71 L50 92 L14 71 L14 29 Z" fill="#0B0B10" />
      {/* Gem — brilliant kesim */}
      <path d="M50 20 L74 38 L66 64 L50 82 L34 64 L26 38 Z" fill={`url(#${id}-face)`} />
      <path d="M26 38 L74 38 L50 20 Z" fill="#FFFFFF" opacity="0.22" />
      <path d="M26 38 L50 46 L74 38 L66 64 L50 82 L34 64 Z" fill="#000" opacity="0.12" />
      <path d="M50 46 L66 64 L50 82 L34 64 Z" fill="#FFFFFF" opacity="0.10" />
      <path d="M34 38 L50 26 L66 38 L50 46 Z" fill="#FFFFFF" opacity="0.28" />
      <circle cx="44" cy="34" r="16" fill={`url(#${id}-shine)`} />
      {/* Qirralar */}
      <path d="M26 38 L50 46 L74 38 M50 46 L50 82 M34 64 L50 46 L66 64" stroke="#FFFFFF" strokeOpacity="0.5" strokeWidth="1" fill="none" />
      {/* Uchqunlar */}
      <path d="M22 18 l1.5 4 4 1.5 -4 1.5 -1.5 4 -1.5 -4 -4 -1.5 4 -1.5 Z" fill="#FFF7CC" />
      <path d="M80 72 l1.2 3 3 1.2 -3 1.2 -1.2 3 -1.2 -3 -3 -1.2 3 -1.2 Z" fill="#FFF7CC" />
      <circle cx="82" cy="22" r="1.4" fill="#FFFFFF" />
    </svg>
  );
}

/**
 * Kategoriya ikonkasi — haqiqiy Telegram NFT gifti (Plush Pepe, Fragment
 * kolleksiyasining eng mashhur pozitsiyasi). Foni olib tashlangan PNG,
 * ortida holografik yorug'lik va oltin uchqun.
 */
function TelegramNft({ size }: { size: number }) {
  return (
    <div className="relative" style={{ width: size, height: size }} aria-hidden>
      <div
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 50% 55%, rgba(168,85,247,0.6), rgba(56,189,248,0.24) 52%, transparent 70%)',
          filter: `blur(${Math.max(4, size * 0.1)}px)`,
        }}
      />
      <img
        src="/nft/cut-plushpepe.png"
        alt=""
        className="absolute inset-0 h-full w-full"
        style={{
          objectFit: 'contain',
          filter: `drop-shadow(0 ${size * 0.06}px ${size * 0.1}px rgba(0,0,0,0.6))`,
        }}
      />
      <span
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          fontSize: Math.max(9, size * 0.2),
          lineHeight: 1,
          color: '#FFE9A3',
          textShadow: '0 0 8px rgba(245,179,1,0.9)',
        }}
      >
        ✦
      </span>
    </div>
  );
}

/* ---------------- Telegram verification — ko'k galochka nishoni ---------------- */
function TelegramVerify({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      aria-hidden
      style={{ filter: 'drop-shadow(0 6px 14px rgba(42,171,238,0.55)) drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
    >
      <defs>
        <linearGradient id="tgVerify" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#5AC8FA" />
          <stop offset="60%" stopColor="#2AABEE" />
          <stop offset="100%" stopColor="#0A6BB5" />
        </linearGradient>
      </defs>
      {/* Tishli (scalloped) nishon */}
      <path
        d="M50 4 L59 12 L71 9 L76 20 L88 24 L86 36 L95 45 L88 55 L90 67 L79 72 L74 84 L62 82 L53 91 L44 84 L32 87 L27 76 L15 72 L17 60 L8 51 L15 42 L13 30 L24 25 L29 13 L41 15 Z"
        fill="url(#tgVerify)"
      />
      <path
        d="M50 12 L57 18 L66 16 L70 25 L79 28 L77 37 L84 44 L79 52 L80 61 L72 65 L68 74 L59 72 L52 79 L45 73 L36 75 L32 67 L24 64 L26 55 L19 48 L25 41 L23 32 L32 28 L36 20 L45 21 Z"
        fill="none"
        stroke="rgba(255,255,255,0.28)"
        strokeWidth="1.5"
      />
      {/* Oq galochka */}
      <path d="M33 50 L45 62 L68 38" stroke="#FFFFFF" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <circle cx="22" cy="18" r="2" fill="#FFF3B0" />
      <circle cx="82" cy="80" r="1.6" fill="#FFF3B0" />
    </svg>
  );
}

/* ---------------- Instagram verified badge ---------------- */
function InstagramBadge({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      aria-hidden
      style={{ filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.45)) drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
    >
      <defs>
        <linearGradient id="igGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#3897F0" />
          <stop offset="100%" stopColor="#0070E0" />
        </linearGradient>
      </defs>
      {/* Official Instagram verified-badge silhouette — solid blue */}
      <path
        d="M50 4 L60 14 L74 10 L75 24 L89 26 L84 39 L96 49 L84 60 L89 73 L75 75 L74 89 L60 85 L50 96 L40 85 L26 89 L25 75 L11 73 L16 60 L4 49 L16 39 L11 26 L25 24 L26 10 L40 14 Z"
        fill="url(#igGrad)"
      />
      <path
        d="M32 51 L44 64 L70 38"
        stroke="#fff"
        strokeWidth="7"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/* ---------------- Telegram Premium ---------------- */
function TelegramPremium({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      aria-hidden
      style={{ filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.45)) drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
    >
      <defs>
        <linearGradient id="premGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#FFF4B3" />
          <stop offset="45%" stopColor="#FFD400" />
          <stop offset="100%" stopColor="#FF8800" />
        </linearGradient>
        <linearGradient id="premShine" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <radialGradient id="premHalo" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FFD400" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#FFD400" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Soft golden halo behind star */}
      <circle cx="50" cy="50" r="48" fill="url(#premHalo)" />

      {/* 5-point star — Telegram Premium (gold/yellow) */}
      <path
        d="M50 8 L61 35 L90 38 L68 57 L74 86 L50 71 L26 86 L32 57 L10 38 L39 35 Z"
        fill="url(#premGrad)"
        stroke="#E07B00"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />

      {/* Inner shine */}
      <path d="M50 18 L57 36 L75 38 L62 50 L65 68 L50 60 Z" fill="url(#premShine)" />

      {/* Small sparkles */}
      <g opacity="0.9">
        <circle cx="86" cy="20" r="2.5" fill="#FFF4B3" />
        <circle cx="14" cy="74" r="2" fill="#FFF4B3" />
        <circle cx="80" cy="76" r="1.5" fill="#FFF4B3" />
      </g>
    </svg>
  );
}

/* ---------------- Telegram Stars ---------------- */
function TelegramStars({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      aria-hidden
      style={{ filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.45)) drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
    >
      <defs>
        <linearGradient id="starGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#FFE066" />
          <stop offset="50%" stopColor="#FFC93C" />
          <stop offset="100%" stopColor="#FF9500" />
        </linearGradient>
        <linearGradient id="starShine" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <symbol id="star5sym" viewBox="0 0 100 100">
          <path d="M50 8 L61 35 L90 38 L68 57 L74 86 L50 71 L26 86 L32 57 L10 38 L39 35 Z" />
        </symbol>
      </defs>

      {/* Outer warm glow */}
      <circle cx="50" cy="50" r="48" fill="#FFC93C" opacity="0.08" />

      {/* Back star — smaller, top-right */}
      <use
        href="#star5sym"
        x="38"
        y="6"
        width="48"
        height="48"
        fill="url(#starGrad)"
        stroke="#FF9500"
        strokeWidth="1.5"
        strokeLinejoin="round"
        opacity="0.95"
      />

      {/* Front star — slightly larger, bottom-left */}
      <use
        href="#star5sym"
        x="10"
        y="32"
        width="58"
        height="58"
        fill="url(#starGrad)"
        stroke="#FF9500"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />

      {/* Front-star inner shine (path scaled+translated to match the front star) */}
      <g transform="translate(10 32) scale(0.58)">
        <path d="M50 18 L57 36 L75 38 L62 50 L65 68 L50 60 Z" fill="url(#starShine)" />
      </g>

      {/* Sparkles around the pair */}
      <g opacity="0.9">
        <circle cx="92" cy="62" r="2" fill="#FFE066" />
        <circle cx="8" cy="24" r="1.5" fill="#FFE066" />
        <circle cx="78" cy="92" r="1.5" fill="#FFE066" />
      </g>
    </svg>
  );
}

/* ============================================================
 *  Gift-specific icons (used by gift products inside the gifts
 *  category — each SKU gets its own icon instead of a box.)
 * ============================================================ */

export type GiftKind = 'rose' | 'heart' | 'default';

export function giftKindFromSku(sku: string | undefined | null): GiftKind {
  if (!sku) return 'default';
  if (sku.includes('ROSE')) return 'rose';
  if (sku.includes('HEART')) return 'heart';
  return 'default';
}

export function GiftIcon({ kind, size = 56 }: { kind: GiftKind; size?: number }) {
  switch (kind) {
    case 'rose':
      return <RoseIcon size={size} />;
    case 'heart':
      return <HeartIcon size={size} />;
    default:
      return <TelegramGifts size={size} />;
  }
}

function RoseIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      aria-hidden
      style={{ filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.45)) drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
    >
      <defs>
        <radialGradient id="roseGrad" cx="40%" cy="30%" r="65%">
          <stop offset="0%" stopColor="#FFB3C8" />
          <stop offset="55%" stopColor="#FF3B7A" />
          <stop offset="100%" stopColor="#8B0E3E" />
        </radialGradient>
        <linearGradient id="roseLeaf" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#66BB6A" />
          <stop offset="100%" stopColor="#1B5E20" />
        </linearGradient>
      </defs>

      {/* Stem */}
      <path d="M50 55 Q49 75 48 92" stroke="#1B5E20" strokeWidth="3" fill="none" strokeLinecap="round" />
      {/* Left leaf */}
      <path d="M48 70 Q36 68 30 62 Q34 76 48 76 Z" fill="url(#roseLeaf)" />
      <path d="M40 67 Q43 70 46 73" stroke="#0F3D11" strokeWidth="1" fill="none" opacity="0.4" />
      {/* Right leaf */}
      <path d="M48 80 Q62 82 68 76 Q63 88 48 86 Z" fill="url(#roseLeaf)" />

      {/* Rose head — outer petals */}
      <circle cx="50" cy="35" r="26" fill="url(#roseGrad)" />
      {/* Inner petal overlay */}
      <path
        d="M50 16 Q66 22 66 36 Q66 50 50 56 Q34 50 34 36 Q34 22 50 16 Z"
        fill="#FF6B9D"
        opacity="0.55"
      />
      {/* Petal swirl */}
      <path
        d="M40 32 Q45 24 50 30 Q55 24 60 32 Q58 40 50 42 Q42 40 40 32 Z"
        fill="#FFC9D9"
        opacity="0.75"
      />
      {/* Bud center */}
      <circle cx="50" cy="35" r="4" fill="#FFE8F0" />
      <circle cx="49" cy="34" r="1.5" fill="#FFB3C8" />
    </svg>
  );
}

function HeartIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      aria-hidden
      style={{ filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.45)) drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
    >
      <defs>
        <radialGradient id="heartGrad" cx="35%" cy="28%" r="75%">
          <stop offset="0%" stopColor="#FF8A80" />
          <stop offset="50%" stopColor="#E53935" />
          <stop offset="100%" stopColor="#8B0000" />
        </radialGradient>
      </defs>

      <path
        d="M50 88
           C 50 88, 14 66, 14 38
           C 14 24, 25 14, 36 14
           C 43 14, 48 18, 50 24
           C 52 18, 57 14, 64 14
           C 75 14, 86 24, 86 38
           C 86 66, 50 88, 50 88 Z"
        fill="url(#heartGrad)"
        stroke="#8B0000"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />

      {/* Glossy highlight */}
      <ellipse
        cx="36"
        cy="32"
        rx="10"
        ry="14"
        fill="#fff"
        opacity="0.35"
        transform="rotate(-25 36 32)"
      />
      <circle cx="33" cy="28" r="3.2" fill="#fff" opacity="0.85" />

      {/* Sparkle */}
      <g opacity="0.85">
        <circle cx="76" cy="22" r="2" fill="#FFE4E1" />
        <circle cx="20" cy="60" r="1.5" fill="#FFE4E1" />
      </g>
    </svg>
  );
}

/* ---------------- Telegram Gifts (default box) ---------------- */
function TelegramGifts({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      aria-hidden
      style={{ filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.45)) drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
    >
      <defs>
        <linearGradient id="giftBox" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#FF6B9D" />
          <stop offset="100%" stopColor="#D4537E" />
        </linearGradient>
        <linearGradient id="giftLid" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#FF85B0" />
          <stop offset="100%" stopColor="#E26090" />
        </linearGradient>
        <linearGradient id="giftRibbon" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#FFE066" />
          <stop offset="100%" stopColor="#FFB347" />
        </linearGradient>
      </defs>
      {/* Box base */}
      <rect x="14" y="44" width="72" height="46" rx="4" fill="url(#giftBox)" />
      {/* Box lid */}
      <rect x="10" y="34" width="80" height="16" rx="3" fill="url(#giftLid)" />
      {/* Vertical ribbon (on box) */}
      <rect x="44" y="44" width="12" height="46" fill="url(#giftRibbon)" />
      {/* Vertical ribbon (on lid) */}
      <rect x="44" y="34" width="12" height="16" fill="url(#giftRibbon)" />
      {/* Bow */}
      <path
        d="M50 34 C 42 24, 30 22, 28 30 C 26 36, 36 38, 50 34 Z"
        fill="url(#giftRibbon)"
      />
      <path
        d="M50 34 C 58 24, 70 22, 72 30 C 74 36, 64 38, 50 34 Z"
        fill="url(#giftRibbon)"
      />
      <circle cx="50" cy="32" r="6" fill="url(#giftRibbon)" />
      <circle cx="50" cy="32" r="3" fill="#D89B2C" />
    </svg>
  );
}
